import type { StorageAdapter } from '../types'

const DB_NAME = 'escribonauta-offline'
const DB_VERSION = 1
const STORE_NAME = 'kv'

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB is not available'))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => {
      reject(request.error ?? new Error('Failed to open IndexedDB'))
    }

    request.onsuccess = () => {
      resolve(request.result)
    }

    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME)
      }
    }
  })
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

/**
 * IndexedDB-backed key/value adapter (primary storage for chapter backups).
 */
export function createIndexedDbAdapter(): StorageAdapter {
  let dbPromise: Promise<IDBDatabase> | null = null
  let database: IDBDatabase | null = null

  const getDb = (): Promise<IDBDatabase> => {
    if (database) return Promise.resolve(database)
    if (!dbPromise) {
      dbPromise = openDatabase()
        .then((db) => {
          database = db
          return db
        })
        .catch((error) => {
          dbPromise = null
          throw error
        })
    }
    return dbPromise
  }

  return {
    async get<T>(key: string): Promise<T | null> {
      const db = await getDb()
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const value = await requestToPromise(store.get(key))
      return (value as T | undefined) ?? null
    },

    async set<T>(key: string, value: T): Promise<void> {
      // When the database is already open, start the transaction before the first
      // await so pagehide can still commit the write.
      const db = database ?? (await getDb())
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      await requestToPromise(store.put(value, key))
    },

    async delete(key: string): Promise<void> {
      const db = await getDb()
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      await requestToPromise(store.delete(key))
    },

    async listKeys(prefix?: string): Promise<string[]> {
      const db = await getDb()
      const tx = db.transaction(STORE_NAME, 'readonly')
      const store = tx.objectStore(STORE_NAME)
      const keys = (await requestToPromise(store.getAllKeys())) as IDBValidKey[]
      const stringKeys = keys.filter((key): key is string => typeof key === 'string')
      if (!prefix) return stringKeys
      return stringKeys.filter((key) => key.startsWith(prefix))
    },

    async clear(): Promise<void> {
      const db = await getDb()
      const tx = db.transaction(STORE_NAME, 'readwrite')
      const store = tx.objectStore(STORE_NAME)
      await requestToPromise(store.clear())
    },
  }
}

export async function isIndexedDbAvailable(): Promise<boolean> {
  if (typeof indexedDB === 'undefined') return false
  try {
    const db = await openDatabase()
    db.close()
    return true
  } catch {
    return false
  }
}
